import { FC } from 'react';
import classNames from 'classnames';

import { useDisabled } from '@hh.ru/magritte-common-use-disabled';
import { useBreakpoint } from '@hh.ru/magritte-ui-breakpoint';
import { TitleProps } from '@hh.ru/magritte-ui-title/types';
import { Text, TextDynamic } from '@hh.ru/magritte-ui-typography';

import styles from './title.less';

const TextTypographyOptionsDesktop = {
    'extra-large': 'paragraph-1-regular',
    large: 'paragraph-1-regular',
    medium: 'paragraph-2-regular',
    small: 'paragraph-2-regular',
} as const;

const TextTypographyOptionsMobile = {
    ...TextTypographyOptionsDesktop,
    small: 'paragraph-3-regular',
} as const;

export const Title: FC<TitleProps> = ({
    style = 'primary',
    size = 'extra-large',
    Element = 'h1',
    alignment = 'left',
    maxLines,
    description,
    children,
    descriptionMaxLines,
    descriptionStyle = 'secondary',
    'data-qa': dataQaContainer = 'title-container',
    dataQaDescription = 'title-description',
    dataQaTitle = 'title',
    ...rest
}) => {
    const isDisabled = useDisabled();
    const { isMobile } = useBreakpoint();

    const textTypographyOptions = isMobile ? TextTypographyOptionsMobile : TextTypographyOptionsDesktop;

    const containerStyles = classNames(isDisabled && styles['text-disabled'], styles[`text-alignment-${alignment}`]);

    const titleStyles = classNames(
        styles.text,
        styles['text-overflow'],
        styles[`text-typography-${size}`],
        styles[`text-style-${style}`]
    );

    const descriptionStyles = classNames(styles.text, styles['text-overflow'], styles['space-description']);

    return (
        <div data-qa={dataQaContainer} className={containerStyles}>
            <Element {...rest} data-qa={dataQaTitle} className={titleStyles} style={{ WebkitLineClamp: maxLines }}>
                {children}
            </Element>
            {!!description && (
                <TextDynamic>
                    <div
                        data-qa={dataQaDescription}
                        className={descriptionStyles}
                        style={{ WebkitLineClamp: descriptionMaxLines }}
                    >
                        <Text typography={textTypographyOptions[size]} style={descriptionStyle}>
                            {description}
                        </Text>
                    </div>
                </TextDynamic>
            )}
        </div>
    );
};
